import { useCallback, useEffect, useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { axiosClient } from './utils/axios';
import { ENTITY_TYPE } from './constants';
import { count } from './utils/metric';
import { useReportingContext } from './components';
import {
    INAPPROPRIATE_CONTENT_OR_BEHAVIOR_SUB_REASONS,
    REPORT_ANONYMOUSLY_CHECKBOX_NAME,
    SUB_REASON_FIELD,
} from './components/IssueForm/steps/constants';

export interface ReportEligibilityResponse {
    is_eligible: boolean;
}

export interface ReportEligibilityResponseItem extends ReportEligibilityResponse {
    entity_id: string;
    reason?: string;
}

export const isItemEligibleToReport = (eligibilityList: ReportEligibilityResponseItem[], entityId: string) =>
    eligibilityList.some((item) => item.entity_id === entityId && item.is_eligible);

export const useCountOnMount = (metricSuffix: string) => {
    const { entityType } = useReportingContext();

    useEffect(() => {
        count(`content_reporting.${entityType.toLowerCase()}.${metricSuffix}`);
    }, [metricSuffix, entityType]);
};

export const useMetricCounter = () => {
    const { entityType } = useReportingContext();

    return useCallback(
        (metricSuffix: string) => {
            const fullMetric = `content_reporting.${entityType.toLowerCase()}.${metricSuffix}`;
            count(fullMetric);
        },
        [entityType]
    );
};

export const useReportingEligibility = (entityType: ENTITY_TYPE, entityIds: string[]) => {
    const [eligibilityList, setEligibilityList] = useState<ReportEligibilityResponseItem[]>([]);
    const count = useMetricCounter();
    const { userId } = getContext();

    const stringifiedEntityIds = useMemo(() => JSON.stringify(entityIds), [entityIds]);

    useEffect(() => {
        if (!userId || entityIds.length === 0) {
            return;
        }

        (async () => {
            try {
                let response;

                switch (entityType) {
                    case ENTITY_TYPE.ATTACHMENT: {
                        response = await axiosClient.get<ReportEligibilityResponseItem[]>(
                            pathfinder(
                                'content_reporting_get_items_bulk_eligibility',
                                {
                                    entity_id: entityIds[0],
                                    entity_type: entityType,
                                },
                                {
                                    query: {
                                        entityId: entityIds.join(','),
                                    },
                                }
                            )
                        );
                        break;
                    }
                    default: {
                        const singleResponse = await axiosClient.get<ReportEligibilityResponseItem>(
                            pathfinder('content_reporting_get_user_eligibility', {
                                entity_id: entityIds[0],
                                entity_type: entityType,
                            })
                        );
                        response = { data: [{ ...singleResponse.data, entity_id: entityIds[0] }] };
                    }
                }

                setEligibilityList(response.data);
            } catch (error) {
                logger.error({ error, message: 'Failed to fetch report eligibility:' });
                count('report_eligibility.error');
            }
        })();
        // eslint-disable-next-line  react-hooks/exhaustive-deps
    }, [entityType, stringifiedEntityIds, count, userId]);

    const disableReporting = (id: string) => {
        setEligibilityList((prev) =>
            prev.map((item) => (item.entity_id === id ? { ...item, is_eligible: false } : item))
        );
    };

    return { eligibilityList, disableReporting };
};

export const useAnonymousCheckbox = (triggerSubReasons: INAPPROPRIATE_CONTENT_OR_BEHAVIOR_SUB_REASONS[]) => {
    const { watch, setValue } = useFormContext();
    const selectedSubReason = watch(SUB_REASON_FIELD);
    const { entityId, entityType } = useReportingContext();
    const [isAnonymousEligible, setIsAnonymousEligible] = useState<boolean | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const response = await axiosClient.get<{ is_eligible: boolean }>(
                    pathfinder('content_reporting_get_user_anonymous_eligibility', {
                        entity_id: entityId,
                        entity_type: entityType,
                    })
                );
                setIsAnonymousEligible(response.data.is_eligible);
            } catch (error) {
                logger.error({ error, message: 'Failed to fetch anonymous eligibility' });
                setIsAnonymousEligible(true);
            }
        })();
    }, [entityId, entityType]);

    const shouldTriggerAnonymous = triggerSubReasons.includes(selectedSubReason);
    const shouldShowAnonymousCheckbox = shouldTriggerAnonymous && isAnonymousEligible === true;

    useEffect(() => {
        if (!shouldShowAnonymousCheckbox) {
            setValue(REPORT_ANONYMOUSLY_CHECKBOX_NAME, false);
        }
    }, [shouldShowAnonymousCheckbox, setValue]);

    return { shouldShowAnonymousCheckbox };
};
